import React, { createContext, useState, useEffect, useCallback, useContext, useRef } from 'react';
import type { ReactNode } from 'react';
import type { User } from 'firebase/auth';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { migrateUserProfileOnLogin } from '../../utils/profileMigration';
import { useInactivityTimeout } from '../../hooks/useInactivityTimeout';
import { presenceService } from '../../services/presenceService';
import { cleanupUserSession } from '../../services/sessionCleanupService';
import { windowSessionCoordinator } from '../../services/windowSessionCoordinator';
import { getFirebaseInstances } from '../../config/firebase/firebase';

export interface AuthContextValue {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  accountStatus: string | null;
  signupFlow: string | null;
  canAccessTerminal: boolean;
  /** User roles loaded from Firestore profile (e.g., ['reviewer', 'admin']) */
  roles: string[];
  /** Check if current user has a specific role */
  hasRole: (role: string) => boolean;
  signOutUser: (reason?: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<string[]>([]);
  const [accountStatus, setAccountStatus] = useState<string | null>(null);
  const [signupFlow, setSignupFlow] = useState<string | null>(null);
  const [canAccessTerminal, setCanAccessTerminal] = useState(false);
  const activeUidRef = useRef<string | null>(null);

  const hasRole = useCallback((role: string) => roles.includes(role), [roles]);

  const signOutUser = useCallback(async (_reason = 'manual') => {
    try {
          const { auth } = await getFirebaseInstances();
          // Capture uid before signOut nulls auth.currentUser
          const uid = auth.currentUser?.uid ?? '';
          cleanupUserSession(uid);
          activeUidRef.current = null;
          await signOut(auth);
    } catch (error) {
      console.warn('Firebase unavailable during sign out:', error);
    } finally {
      setUser(null);
      setRoles([]);
      setAccountStatus(null);
      setSignupFlow(null);
      setCanAccessTerminal(false);
    }
  }, []);

  // Auto-logout authenticated users on inactivity.
  useInactivityTimeout(async () => {
    try {
      const { auth } = await getFirebaseInstances();
      if (!auth.currentUser) return;
      await signOutUser('inactivity');
    } catch (error) {
      console.error('Inactivity logout failed:', error);
    }
  });

  useEffect(() => {
    let mounted = true;
    let unsubscribe: (() => void) | undefined;

    const initializeAuth = async () => {
      try {
        const { auth } = await getFirebaseInstances();
        if (!mounted) return;

        unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        activeUidRef.current = user.uid;
        windowSessionCoordinator.initialize();

        // Attempt to migrate profile if needed (with error handling)
        try {
          await migrateUserProfileOnLogin(user);
        } catch (migrationError) {
          console.warn('Profile migration failed but continuing with authentication:', migrationError);
        }

        // Load user roles from Firestore profile
        try {
          const { db } = await getFirebaseInstances();
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          const userData = userDoc.exists() ? userDoc.data() : null;
          setRoles(Array.isArray(userData?.roles) ? userData.roles : []);
          setAccountStatus(userData?.accountStatus || (userData ? null : 'support_required'));
          setSignupFlow(userData?.signupFlow || null);
          setCanAccessTerminal(Boolean(userData) && (
            userData.signupFlow !== 'beta_invite' ||
            userData.accountStatus === 'active'
          ));
        } catch (rolesError) {
          console.warn('Failed to load user roles:', rolesError);
          setRoles([]);
          setAccountStatus('support_required');
          setSignupFlow(null);
          setCanAccessTerminal(false);
        }

        // Initialize presence tracking for authenticated user
        try {
          await presenceService.initializePresence(user.uid);
        } catch (presenceError) {
          console.warn('Failed to initialize presence tracking:', presenceError);
        }
      } else {
        const priorUid = activeUidRef.current;
        if (priorUid) {
          cleanupUserSession(priorUid);
          activeUidRef.current = null;
        }
        windowSessionCoordinator.destroy();
        setRoles([]);
        setAccountStatus(null);
        setSignupFlow(null);
        setCanAccessTerminal(false);

        // Stop presence tracking when user logs out
        try {
          await presenceService.stopPresence();
        } catch (presenceError) {
          console.warn('Failed to stop presence tracking:', presenceError);
        }
      }
      setUser(user);
      setLoading(false);
    });
      } catch (error) {
        console.warn('Firebase Auth unavailable; continuing in unauthenticated review mode:', error);
        if (!mounted) return;
        windowSessionCoordinator.destroy();
        setUser(null);
        setRoles([]);
        setAccountStatus(null);
        setSignupFlow(null);
        setCanAccessTerminal(false);
        setLoading(false);
      }
    };

    initializeAuth();

    // Handle visibility changes for presence tracking only.
    const handleVisibilityChange = async () => {
      try {
        const { auth } = await getFirebaseInstances();
        const currentUser = auth.currentUser;
        if (!currentUser) return;

        if (document.visibilityState === 'hidden') {
          // User switched tabs or minimized - mark as away
          await presenceService.setUserPresence(currentUser.uid, 'away');
        } else if (document.visibilityState === 'visible') {
          // User returned - mark as online
          await presenceService.setUserPresence(currentUser.uid, 'online');
        }
      } catch (error) {
        console.warn('Failed to update presence on visibility change:', error);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mounted = false;
      unsubscribe?.();
      windowSessionCoordinator.destroy();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated: !!user,
      accountStatus,
      signupFlow,
      canAccessTerminal,
      roles,
      hasRole,
      signOutUser,
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
